import { AsyncEvent } from "../util";

type MusicKitInstance = any;

declare global {
  interface Window {
    MusicKit: {
      configure: (config: any) => Promise<void>;
      getInstance: () => MusicKitInstance;
    };
  }
}

export class AppleMusic {
  loaded = false;
  public async init(): Promise<void> {
    if (this.loaded) {
      return;
    }
    const loadedEvent = new AsyncEvent();
    document.addEventListener("musickitloaded", async () => {
      const token: string =
        (
          document.querySelector(
            'meta[name="apple-music-developer-token"]'
          ) as HTMLMetaElement | null
        )?.content || "";

      const callback = this.callbackUrlFix();

      // @ts-ignore
      await window.MusicKit.configure({
        developerToken: token,
        app: {
          name: "UMG",
          build: "1",
        },
      });
      if (callback) {
        window.history.replaceState("", document.title, callback);
      }
      loadedEvent.trigger();
      this.loaded = true;
    });

    this.injectScript();

    await loadedEvent.wait();

    console.debug("Musickit loaded");
    console.debug("Musickit authorized", this.instance.isAuthorized);
  }

  private callbackUrlFix(): string | null {
    // When browser doesn't support popups (e.g. IG browser)
    // musickit redirects back to previous URL
    // with a base64 encoded token in hash string
    // The hash string is appended to existing hash string
    // which conflicts with hash routing in SPAs
    // So this temporarily removes the app route hash (if any)
    // before configuring musickit
    // The original URL without apple token is returned
    // to redirect to after musickit init
    let hashToken;
    const hashParts = window.location.hash.split("#");
    const urlToken = hashParts[hashParts.length - 1] || "";
    try {
      hashToken = window.atob(urlToken);
    } catch (err) {
      return null;
    }
    if (!hashToken) {
      return null;
    }
    const originalUrl = window.location.href;
    const url = new URL(originalUrl);
    url.hash = urlToken;
    const newUrl = url.toString();
    window.history.replaceState("", document.title, newUrl);
    url.hash = hashParts[1] || "";
    return url.toString();
  }

  public get isAuthorized(): boolean {
    return this.instance?.isAuthorized || false;
  }

  public async authorize(): Promise<string> {
    return await this.instance.authorize();
  }

  public async unauthorize() {
    if (!this.instance) {
      return;
    }
    try {
      return await this.instance.unauthorize();
    } catch (err) {
      console.warn("Musickit logout error", err);
    }
  }

  public get musicUserToken(): string | null {
    return this.instance?.musicUserToken || null;
  }

  public get musicKit(): MusicKitInstance {
    return this.instance;
  }

  private get instance(): MusicKitInstance {
    return window.MusicKit.getInstance();
  }

  private injectScript() {
    const s = document.createElement("script", {});
    s.src = "https://js-cdn.music.apple.com/musickit/v3/musickit.js";
    document.head.insertBefore(s, document.head.firstElementChild);
  }
}

export default new AppleMusic();
